<script>
  import { createEventDispatcher } from "svelte";
  import { fade, slide } from "svelte/transition";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";

  export let isOpen = false;
  export let job = null;
  export let quote = null;
  export let isDeleting = false;
  export let errorMessage = "";

  const dispatch = createEventDispatcher();

  function handleClose() {
    if (!isDeleting) {
      dispatch("close");
    }
  }

  function handleConfirm() {
    if (!isDeleting) {
      dispatch("confirm");
    }
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="delete-modal-backdrop"
    role="presentation"
    on:click={handleClose}
    transition:fade={{ duration: 150 }}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="delete-modal-card"
      role="dialog"
      aria-modal="true"
      on:click|stopPropagation
      transition:slide={{ duration: 200 }}
    >
      <div class="delete-modal-header">
        <div class="delete-title-row">
          <WarningFilled size={22} class="danger-header-icon" />
          <h3 class="delete-modal-title">Delete Job Confirmation</h3>
        </div>
        <button
          class="modal-close-btn"
          type="button"
          on:click={handleClose}
          disabled={isDeleting}
          aria-label="Close"
        >
          <Close size={18} />
        </button>
      </div>

      <div class="delete-modal-body">
        {#if job}
          <p class="delete-prompt-text">
            Are you sure you want to permanently delete
            <strong>Job #{job?.__job_number__ || job?.id}</strong>
            {#if job?.customer?.name || (typeof job?.customer === 'string' ? job.customer : null) || quote?.customer_summary?.name || quote?.customer?.name}
              for <strong>{job?.customer?.name || (typeof job?.customer === 'string' ? job.customer : null) || quote?.customer_summary?.name || quote?.customer?.name}</strong>
            {/if}?
          </p>

          <div class="delete-warning-box">
            <WarningFilled size={20} class="warning-box-icon" />
            <div class="delete-warning-text">
              <strong>Permanent Action — Cannot Be Recovered</strong>
              <span>Once deleted, this job cannot be recovered. All associated material lines, scheduling parameters, and truck allocations will be permanently removed from the operational database.</span>
            </div>
          </div>
        {:else}
          <p class="delete-prompt-text">
            Are you sure you want to discard this job setup for
            <strong>Quote #{quote?.quote_number || quote?.id || "Draft"}</strong>
            {#if quote?.customer_summary?.name || quote?.customer?.name}
              for <strong>{quote?.customer_summary?.name || quote?.customer?.name}</strong>
            {/if}?
          </p>

          <div class="delete-warning-box">
            <WarningFilled size={20} class="warning-box-icon" />
            <div class="delete-warning-text">
              <strong>Unsaved Job Configuration</strong>
              <span>This job has not been saved to the database yet. Discarding will clear all truck allocations and dispatch parameters configured on this page.</span>
            </div>
          </div>
        {/if}

        {#if errorMessage}
          <div class="alert alert-danger" transition:slide>
            <WarningFilled size={18} />
            <span>{errorMessage}</span>
          </div>
        {/if}
      </div>

      <div class="delete-modal-footer">
        <button
          class="btn btn-outline"
          type="button"
          on:click={handleClose}
          disabled={isDeleting}
        >
          Cancel
        </button>
        <button
          class="btn btn-danger btn-confirm-delete"
          type="button"
          on:click={handleConfirm}
          disabled={isDeleting}
        >
          {#if isDeleting}
            <span class="btn-spinner" />
            <span>Deleting Job...</span>
          {:else}
            <TrashCan size={16} />
            <span>{job ? "Yes, Delete Job" : "Yes, Discard Job"}</span>
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .btn-danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #b91c1c;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    transition: all 0.15s ease;
  }

  .btn-danger:hover:not(:disabled) {
    background: #b91c1c;
    border-color: #991b1b;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
  }

  .btn-danger:active:not(:disabled) {
    transform: translateY(1px);
  }

  .btn-danger:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .btn-outline {
    background: transparent;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    color: var(--tt-slate-700, #334155);
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    font-weight: 600;
    transition: all 0.15s ease;
  }

  .btn-outline:hover:not(:disabled) {
    background: var(--tt-slate-100, #f1f5f9);
    border-color: var(--tt-slate-400, #94a3b8);
  }

  .delete-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
  }

  .delete-modal-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 520px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .delete-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.1rem 1.4rem;
    background: #fef2f2;
    border-bottom: 1px solid #fee2e2;
  }

  .delete-title-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  :global(.danger-header-icon) {
    color: #dc2626;
    flex-shrink: 0;
  }

  .delete-modal-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #991b1b;
  }

  .modal-close-btn {
    background: transparent;
    border: none;
    color: var(--tt-slate-500, #64748b);
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-close-btn:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
    color: var(--tt-slate-800, #1e293b);
  }

  .delete-modal-body {
    padding: 1.4rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .delete-prompt-text {
    margin: 0;
    font-size: 0.92rem;
    color: var(--tt-slate-700, #334155);
    line-height: 1.5;
  }

  .delete-warning-box {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.9rem 1.1rem;
    background: #fff1f2;
    border: 1px solid #fecdd3;
    border-radius: 8px;
    color: #9f1239;
    font-size: 0.84rem;
    line-height: 1.45;
  }

  :global(.warning-box-icon) {
    color: #e11d48;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .delete-warning-text strong {
    display: block;
    font-weight: 700;
    margin-bottom: 0.2rem;
    color: #be123c;
  }

  .delete-warning-text span {
    display: block;
    color: #881337;
  }

  .alert-danger {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    color: #b91c1c;
    font-size: 0.88rem;
  }

  .delete-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.4rem;
    background: var(--tt-slate-50, #f8fafc);
    border-top: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .btn-confirm-delete {
    padding: 0.55rem 1.25rem;
    font-weight: 700;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
